import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useData } from '../contexts/DataContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { formatZAR } from '../utils/currency';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
export function FavoritesPage() {
  const {
    products
  } = useData();
  const {
    favorites,
    toggleFavorite,
    getFavoriteProducts
  } = useFavorites();
  const {
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  const favoriteProducts = getFavoriteProducts(products);
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);
  return <div className="min-h-screen bg-white" data-id="element-2364">
      <Navbar data-id="element-2365" />

      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2366">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-2367">
          <div className="flex items-center justify-between mb-8" data-id="element-2368">
            <div data-id="element-2369">
              <h1 className="font-serif text-4xl font-bold text-gray-900 mb-2" data-id="element-2370">
                My Favorites
              </h1>
              <p className="text-gray-600" data-id="element-2371">
                {favoriteProducts.length}{' '}
                {favoriteProducts.length === 1 ? 'item' : 'items'} saved
              </p>
            </div>
            <Heart className="w-12 h-12 text-rose-500 fill-current" data-id="element-2372" />
          </div>

          {favoriteProducts.length === 0 ? <div className="text-center py-20" data-id="element-2373">
              <div className="w-24 h-24 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2374">
                <Heart className="w-12 h-12 text-rose-300" data-id="element-2375" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-3" data-id="element-2376">
                No favorites yet
              </h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto" data-id="element-2377">
                Start adding products to your favorites by clicking the heart
                icon on any product.
              </p>
              <Button onClick={() => navigate('/')} size="lg" data-id="element-2378">
                Browse Products
              </Button>
            </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-2379">
              {favoriteProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-rose-50" data-id="element-2380">
                  <div className="relative aspect-square overflow-hidden bg-gray-100" data-id="element-2381">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" data-id="element-2382" />
                    {product.isNew && <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-rose-500 shadow-sm" data-id="element-2383">
                        NEW
                      </div>}
                    <button onClick={() => toggleFavorite(product.id)} className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-md text-rose-500 hover:bg-rose-50 transition-colors" data-id="element-2384">
                      <Heart className="w-5 h-5 fill-current" data-id="element-2385" />
                    </button>
                  </div>

                  <div className="p-5" data-id="element-2386">
                    <div className="text-xs font-medium text-rose-500 mb-1 uppercase tracking-wider" data-id="element-2387">
                      {product.category}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]" data-id="element-2388">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-4" data-id="element-2389">
                      <span className="text-xl font-bold text-gray-900" data-id="element-2390">
                        {formatZAR(product.price)}
                      </span>
                      {product.stock > 0 ? <span className="text-xs text-green-600 font-medium" data-id="element-2391">
                          In Stock
                        </span> : <span className="text-xs text-red-600 font-medium" data-id="element-2392">
                          Out of Stock
                        </span>}
                    </div>

                    <div className="flex gap-2" data-id="element-2393">
                      <Button onClick={() => navigate('/occasions')} className="flex-1" size="sm" leftIcon={<ShoppingBag className="w-4 h-4" data-id="element-2395" />} data-id="element-2394">
                        Add to Cart
                      </Button>
                      <Button onClick={() => toggleFavorite(product.id)} variant="outline" size="sm" className="text-rose-500 border-rose-200 hover:bg-rose-50" data-id="element-2396">
                        <Trash2 className="w-4 h-4" data-id="element-2397" />
                      </Button>
                    </div>
                  </div>
                </motion.div>)}
            </div>}
        </motion.div>
      </section>

      <Footer data-id="element-2398" />
    </div>;
}